/**
 * Statsig Abstraction for Proof of Value (POV)
 *
 * This module provides a custom abstraction layer over Statsig hooks to handle
 * the conditional logic during our proof of value phase. It ensures that:
 *
 * 1. When Statsig is disabled via feature flags, safe fallback values are returned
 * 2. When in Shopify design mode, Statsig is automatically disabled
 * 3. Components can use Statsig hooks without worrying about initialization state
 *
 * Usage:
 *
 * Instead of importing from '@statsig/react-bindings':
 * ```typescript
 * import { useStatsigClient, useStatsigUser } from '@statsig/react-bindings'
 * ```
 *
 * Import from this abstraction:
 * ```typescript
 * import { useStatsigClient, useStatsigUser } from '@/hooks/useStatsig'
 * ```
 */

import {
  useStatsigClient as useStatsigClientOriginal,
  useStatsigUser as useStatsigUserOriginal,
  StatsigUser,
  Experiment,
  FeatureGate,
  type FeatureGateEvaluationOptions,
  type ExperimentEvaluationOptions,
} from '@statsig/react-bindings'
import { useShouldEnableExperimentation } from '../useShouldEnableExperimentation'
import { useCallback } from 'react'
import { User } from '@lounge-fe/auth/src/vanilla/types'
import { parseGID } from '@/utils'
import isEqual from 'lodash/isEqual'

export type { StatsigUser }

export const useStatsigClient = () => {
  const shouldEnableExperimentation = useShouldEnableExperimentation()

  try {
    if (shouldEnableExperimentation) {
      // Use the actual Statsig client when enabled
      return useStatsigClientOriginal()
    }
  } catch (error) {
    console.warn('Statsig client not available:', error)
  }

  // Return undefined client when Statsig is disabled
  return {
    client: undefined,
  }
}

export const useStatsigUser = () => {
  const shouldEnableExperimentation = useShouldEnableExperimentation()

  try {
    if (shouldEnableExperimentation) {
      return useStatsigUserOriginal()
    }
  } catch (error) {
    console.warn('Statsig user not available:', error)
  }

  return {
    user: null,
  }
}

export const useStatsig = () => {
  const { client } = useStatsigClient()

  /**
   * Handles the Statsig-specific parts of user identification while providing fallback behavior
   * Only performs Statsig operations when the client is available
   *
   * TODO: Add cross-domain tracking cookie, we'll need to save the stableID in a cookie if the user has consented to tracking
   */
  const identifyUser = useCallback(
    (user: User | null) => {
      // Early return if Statsig client is not available
      if (!client) {
        return
      }

      try {
        const { stableID, user: initialStatsigUser } = client.getContext()
        const statsigUser: StatsigUser = {
          ...initialStatsigUser,
          customIDs: {
            stableID: stableID ?? undefined,
          },
        }

        if (user) {
          const userID = parseGID(user.id).childObjectId

          // Build complete Statsig user object
          Object.assign(statsigUser, {
            userID,
            email: user.email,
            customIDs: {
              stableID: stableID ?? undefined,
            },
          })

          // Only update if the user data has actually changed
          if (isEqual(initialStatsigUser, statsigUser)) {
            return
          }

          // Update Statsig with user information
          client.updateUserSync(statsigUser)
        }

        // Store Statsig user in localStorage only when Statsig is enabled
        localStorage.setItem('lounge.statsig.user', JSON.stringify(statsigUser))
      } catch (error) {
        console.warn('Failed to identify Statsig user:', error)
      }
    },
    [client?.loadingStatus],
  )

  const getExperiment = useCallback(
    (
      experimentName: string,
      options?: ExperimentEvaluationOptions,
    ): Experiment | undefined => {
      if (!client) {
        return
      }

      return client.getExperiment(experimentName, options)
    },
    [client?.loadingStatus],
  )

  const getFeatureGate = useCallback(
    (
      featureGateName: string,
      options?: FeatureGateEvaluationOptions,
    ): FeatureGate | undefined => {
      if (!client) {
        return
      }

      return client.getFeatureGate(featureGateName, options)
    },
    [client?.loadingStatus],
  )

  return { identifyUser, getExperiment, getFeatureGate }
}
